import React, { useEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import { brand } from '../../theme';

interface LibraryPosterProps {
  /** The video to take a frame from. Without one, the placeholder shows. */
  src?: string | null;
  /** Overlays positioned inside the poster, e.g. a "New" chip. */
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/** Take the frame half a second in: many clips open on black or a fade. */
const FRAME_AT_SECONDS = 0.5;

const frameSource = (src: string) => (src.includes('#') ? src : `${src}#t=${FRAME_AT_SECONDS}`);

/**
 * A 16:9 poster for a library item: a real frame from the video, read with
 * preload="metadata" once the poster comes near the viewport (so a long
 * library doesn't fetch every file at once). Until the frame arrives, or when
 * the browser can't read the file, a neutral placeholder shows instead of a
 * blank black box.
 */
const LibraryPoster: React.FC<LibraryPosterProps> = ({ src, children, sx }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setReady(false);
    setFailed(false);
  }, [src]);

  useEffect(() => {
    if (nearViewport) return undefined;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [nearViewport]);

  const showVideo = Boolean(src) && nearViewport && !failed;
  const showFrame = showVideo && ready;

  return (
    <Box
      ref={containerRef}
      sx={[
        {
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // The frame letterboxes on ink, like a player; the placeholder is neutral.
          bgcolor: showFrame ? brand.ink : 'grey.100',
          transition: 'background-color 0.2s ease',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {!showFrame && <MovieOutlinedIcon aria-hidden="true" sx={{ fontSize: 30, color: 'grey.500' }} />}
      {showVideo && (
        // A still frame only: muted, never played, hidden from assistive tech.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src={frameSource(src as string)}
          preload="metadata"
          muted
          playsInline
          tabIndex={-1}
          aria-hidden="true"
          data-testid="library-poster-video"
          onLoadedData={() => setReady(true)}
          onError={() => setFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: ready ? 1 : 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
          }}
        />
      )}
      {children}
    </Box>
  );
};

export default LibraryPoster;
